import React, { useState } from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Info, ChevronDown, ChevronUp } from 'lucide-react';

const scoreColor = (v) => (v >= 75 ? 'bg-emerald-500' : v >= 55 ? 'bg-amber-500' : 'bg-rose-500');
const scoreTextColor = (v) => (v >= 75 ? 'text-emerald-600' : v >= 55 ? 'text-amber-600' : 'text-rose-600');

const ScoreCriterion = ({ label, value, weight }) => (
  <div data-testid={`score-criterion-${label.toLowerCase()}`}>
    <div className="flex items-center justify-between mb-1">
      <span className="text-xs text-slate-600">
        {label} <span className="text-slate-400">({weight}%)</span>
      </span>
      <span className={`text-xs font-semibold ${scoreTextColor(value)}`}>{value}</span>
    </div>
    <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
      <div className={`h-full ${scoreColor(value)} transition-all duration-500`} style={{ width: `${value}%` }} />
    </div>
  </div>
);

const ScoreBreakdown = ({ breakdown, t }) => {
  const [expanded, setExpanded] = useState(false);
  if (!breakdown) return null;

  const total = breakdown.total;
  const weights = breakdown.weights || { price: 40, moq: 20, delivery: 20, reliability: 20 };
  const criteria = [
    { key: 'price', label: t('scorePrice'), value: breakdown.price, weight: weights.price },
    { key: 'moq', label: t('scoreMoq'), value: breakdown.moq, weight: weights.moq },
    { key: 'delivery', label: t('scoreDelivery'), value: breakdown.delivery, weight: weights.delivery },
    { key: 'reliability', label: t('scoreReliability'), value: breakdown.reliability, weight: weights.reliability },
  ];

  return (
    <div className="bg-slate-50 rounded-lg border border-slate-200 p-4" data-testid="score-breakdown">
      <div className="flex items-center justify-between gap-3 mb-3">
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-3 cursor-help" data-testid="score-total">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center border-2 ${total >= 75 ? 'border-emerald-500 bg-emerald-50' : total >= 55 ? 'border-amber-500 bg-amber-50' : 'border-rose-500 bg-rose-50'}`}>
                  <span className={`text-lg font-bold ${scoreTextColor(total)}`}>{total}</span>
                </div>
                <div>
                  <div className="text-xs text-slate-500">{t('overallScore')}</div>
                  <div className="text-sm font-medium text-slate-700 flex items-center gap-1">
                    {t('scoreBreakdown')} <Info className="w-3 h-3 text-slate-400" />
                  </div>
                </div>
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs text-xs space-y-1.5 p-3">
              <p className="font-semibold">{t('scoreHelpTitle')}</p>
              <p className="text-slate-300">{t('scoreHelpIntro')}</p>
              <ul className="space-y-1 text-slate-200">
                <li>• {t('scoreHelpPrice')}</li>
                <li>• {t('scoreHelpMoq')}</li>
                <li>• {t('scoreHelpDelivery')}</li>
                <li>• {t('scoreHelpReliability')}</li>
              </ul>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="hidden md:inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"
          data-testid="score-toggle-button"
        >
          {expanded ? <>{t('hideDetails')} <ChevronUp className="w-3 h-3" /></> : <>{t('viewDetails')} <ChevronDown className="w-3 h-3" /></>}
        </button>
      </div>

      <div className={`space-y-2.5 ${expanded ? 'block' : 'block md:hidden'}`} data-testid="score-criteria-list">
        {criteria.map((c) => (
          <ScoreCriterion key={c.key} label={c.label} value={c.value} weight={c.weight} />
        ))}
      </div>
    </div>
  );
};

export default ScoreBreakdown;
